/**
 * ThemeContext
 * Gère les thèmes Day / Night / System pour toute l'application.
 * - Lit localStorage immédiatement (pas de flash).
 * - Sync Firebase en arrière-plan si l'utilisateur est connecté.
 * - Écoute les changements Firestore pour synchroniser entre appareils.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { auth, onAuthStateChanged } from '../services';
import {
  getThemePreference,
  saveThemePreference,
  subscribeThemePreference,
} from '../services/theme.service';

export type ThemePreference = 'day' | 'night' | 'system';
export type ResolvedTheme = 'day' | 'night';

interface ThemeContextValue {
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
  isReady: boolean;
}

const THEME_STORAGE_KEY = 'rabbit-theme-preference';

// Interrupteur « night forcé » (beta fermée) : true = night sur TOUTE l'app
// (landing, popins login/signup, loader, /projects…), préférences
// localStorage/Firestore et toggle ignorés. false (actuel) = Day/Night/System
// actifs — le toggle « Day mode » du menu Display fonctionne et la préférence
// est persistée. Miroir : applyInitialTheme dans src/index.js.
const FORCE_NIGHT_BETA = false;

const ThemeContext = createContext<ThemeContextValue | null>(null);

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'night';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'day' : 'night';
};

const resolveTheme = (theme: ThemePreference): ResolvedTheme => {
  if (theme === 'system') return getSystemTheme();
  return theme;
};

const applyThemeToDom = (resolvedTheme: ResolvedTheme) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolvedTheme);
};

const getStoredTheme = (): ThemePreference | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'day' || stored === 'night' || stored === 'system') return stored;
  } catch {
    // localStorage peut être indisponible (private mode, etc.)
  }
  return null;
};

const storeTheme = (theme: ThemePreference) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Ignore
  }
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Beta fermée : le night est le thème par défaut (landing beta, popins
  // login/signup, loader). La préférence stockée/Firestore ne prime que
  // pour un utilisateur identifié (sync ci-dessous).
  const [theme, setThemeState] = useState<ThemePreference>('night');
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('night');
  const [isReady, setIsReady] = useState(false);

  // Met à jour le thème résolu et le DOM
  const applyTheme = useCallback((nextTheme: ThemePreference) => {
    const resolved = resolveTheme(nextTheme);
    setResolvedTheme(resolved);
    applyThemeToDom(resolved);
  }, []);

  // Changement de thème explicite (toggle utilisateur)
  const setTheme = useCallback(
    async (nextTheme: ThemePreference) => {
      // Night forcé (beta) : le toggle est inerte tant que FORCE_NIGHT_BETA
      // est true — réactivé depuis la fin de la beta fermée.
      if (FORCE_NIGHT_BETA) return;

      setThemeState(nextTheme);
      storeTheme(nextTheme);
      applyTheme(nextTheme);

      try {
        await saveThemePreference(nextTheme);
      } catch (error) {
        // On ne bloque pas l'UI si Firebase échoue
        console.error('[Theme] Failed to save preference:', error);
      }
    },
    [applyTheme]
  );

  // Initialisation : localStorage d'abord pour éviter le flash,
  // night par défaut (beta fermée) si rien n'est stocké
  useEffect(() => {
    const stored = getStoredTheme();
    const initialTheme = FORCE_NIGHT_BETA ? 'night' : (stored ?? 'night');
    setThemeState(initialTheme);
    applyTheme(initialTheme);
    setIsReady(true);
  }, [applyTheme]);

  // Écoute les changements de préférence système
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handleChange = () => {
      if (theme === 'system') {
        applyTheme('system');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, applyTheme]);

  // Sync Firebase : charge la préférence utilisateur et souscrit aux changements
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = undefined;
      }

      // Beta fermée : night forcé pour tout le monde, connecté ou non —
      // la préférence Firestore n'est ni chargée ni appliquée.
      if (FORCE_NIGHT_BETA) {
        setThemeState('night');
        applyTheme('night');
        return;
      }

      if (!firebaseUser) {
        // Déconnecté (landing beta, popins login/signup, loader) : night
        // forcé, quelle que soit la préférence stockée. Non persisté —
        // la préférence Firestore reprend la main à la connexion.
        setThemeState('night');
        applyTheme('night');
        return;
      }

      // 1. Charge la préférence Firestore
      try {
        const remoteTheme = await getThemePreference(firebaseUser.uid);
        if (remoteTheme) {
          setThemeState(remoteTheme);
          storeTheme(remoteTheme);
          applyTheme(remoteTheme);
        } else {
          // Pas de préférence distante (nouveau compte) : ré-applique la
          // préférence locale éventuelle, écrasée par le night forcé tant
          // que l'utilisateur était déconnecté.
          const stored = getStoredTheme();
          if (stored) {
            setThemeState(stored);
            applyTheme(stored);
          }
        }
      } catch (error) {
        console.error('[Theme] Failed to load remote preference:', error);
      }

      // 2. Souscrit aux changements futurs (cross-device)
      unsubscribe = subscribeThemePreference(firebaseUser.uid, (remoteTheme) => {
        if (remoteTheme) {
          setThemeState(remoteTheme);
          storeTheme(remoteTheme);
          applyTheme(remoteTheme);
        }
      });
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribe) unsubscribe();
    };
  }, [applyTheme]);

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
      isReady,
    }),
    [theme, resolvedTheme, setTheme, isReady]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
